import {CopilotWidgetType} from 'common/src/modules/shared/copilot/copilot.enums';
import {isoDateUtcSchema} from 'common/src/modules/shared/copilot/copilot.schemas';
import {AiDashboardWidgetType} from 'common/src/modules/dashboards/dashboards.types';
import {z} from 'zod';
import {AiAnalyticsEventType, AnalyticsEventType, MetricValue} from '../analytics.types';

const copilotAnalyticsWidgetSchema = z.object({
  dashboardId: z.number().describe('The ID of the dashboard to get widgets from'),
  dimension: z.string().describe('The viewing dimension'),
  metrics: z
    .array(z.nativeEnum(MetricValue))
    .describe(
      'The requested metrics. A maximum of 2 metrics is allowed to ensure clarity in data visualization and alignment with sorting logic. Returned data will be sorted by the first metric.',
    )
    .max(2, 'Only up to 2 metrics are allowed'),
  breakdownByTime: z.boolean().describe('If true, metrics will be split over days.'),
  limit: z
    .number()
    .describe(
      'The number of results to return. Applicable only when the selected dimension is provided and breakDownByTime is false.',
    )
    .nullable()
    .optional(),
  type: z.nativeEnum(AiDashboardWidgetType),
  eventType: z
    .nativeEnum(AiAnalyticsEventType)
    .default(AiAnalyticsEventType.Adserving)
    .describe('Analytics event type'),
  withTrends: z.boolean().describe('Whether to include trends in the data').nullable().optional(),
});

const copilotAnalyticsTableWidgetInputSchema = z.object({
  dimensions: z
    .array(z.string())
    .min(1)
    .max(3)
    .describe(
      'The requested dimensions (at least one dimension is required). Must be taken from the "Available dimensions" list.',
    ),
  metrics: z
    .array(z.string())
    .min(1)
    .max(10)
    .describe('The Requested metrics. Must be taken from the "Available metrics" list'),
  startDate: isoDateUtcSchema
    .describe('Requested start date (start of the day)')
    .nullable()
    .optional(),
  endDate: isoDateUtcSchema.describe('Requested end date (end of the day)').nullable().optional(),
  filter: z
    .object({
      dimension: z.string().describe('The filtering dimension'),
      values: z.array(z.string()),
    })
    .nullable()
    .optional(),
  eventType: z
    .nativeEnum(AnalyticsEventType)
    .describe('The event type')
    .default(AnalyticsEventType.Adserving),
  withTrends: z.boolean().describe('Whether to include trends in the data').nullable().optional(),
});

const inlineWidgetSchema = z.object({
  type: z.literal(CopilotWidgetType.InlineWidget),
  content: z
    .object({
      title: z
        .string()
        .describe(
          'A title which contains metrics and dimensions. The number of metrics in the title MUST NEVER be greater than 2',
        ),
      metrics: z.array(z.string()).max(2).min(1),
      dimensions: z.array(z.string()).describe('The viewing dimensions'),
      type: z.nativeEnum(AiDashboardWidgetType),
      breakdownByTime: z
        .boolean()
        .describe(
          'If true, the data will be split over time (e.g., daily). This can only be enabled when exactly one metric is selected. If multiple metrics are provided, this must be false.',
        ),
      analyticsData: z
        .array(z.record(z.union([z.string(), z.number()])))
        .nonempty('Analytics data must include at least one record of actual data')
        .nullable()
        .optional()
        .describe(
          `keys and values MUST be taken ONLY from the result of the used tool. NEVER add any other keys!`,
        ),
      id: z.number().describe('The ID of the widget'),
      campaignId: z.number(),
      dimensionValues: z
        .record(
          z.object({
            uiName: z.string(),
            value: z.string(),
          }),
        )
        .nullable()
        .optional(),
      dimensionFilterValues: z
        .array(z.record(z.union([z.string(), z.string()])))
        .nonempty()
        .nullable(),
      startDate: isoDateUtcSchema.describe('Requested start date'),
      endDate: isoDateUtcSchema.describe('Requested end date'),
      eventType: z.nativeEnum(AiAnalyticsEventType).describe('Analytics event type'),
      withTrends: z.boolean().describe('Whether to include trends in the data'),
    })
    .describe(
      'Inline Widget - shows query result in a visual way. Cannot visualize more than 2 metrics',
    ),
});

export {copilotAnalyticsWidgetSchema, inlineWidgetSchema, copilotAnalyticsTableWidgetInputSchema};
